import React, { useRef, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { setSplitRatio } from "../../state/actions";
import "./verticalresizer.style.scss";
import splitIcon from "../../images/split.png";

type Props = {
  splitRatio: number;
  setSplitRatio: (ratio: number) => void;
  isDrawingStarted?: boolean;
  timerDone?: boolean;
};

const VerticalResizer = ({
  splitRatio,
  setSplitRatio,
  isDrawingStarted,
  timerDone,
}: Props) => {
  const dragging = useRef(false);
  const [isPortrait, setIsPortrait] = useState<boolean>(() => {
    const sw =
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      window.innerWidth;
    const sh =
      document.documentElement.clientHeight ||
      document.body.clientHeight ||
      window.innerHeight;
    return sw < sh;
  });

  useEffect(() => {
    const onResize = () => {
      const sw =
        document.documentElement.clientWidth ||
        document.body.clientWidth ||
        window.innerWidth;
      const sh =
        document.documentElement.clientHeight ||
        document.body.clientHeight ||
        window.innerHeight;
      setIsPortrait(sw < sh);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      let clientX: number;
      let clientY: number;
      if (e instanceof TouchEvent) {
        if (e.touches.length < 1) return;
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = (e as MouseEvent).clientX;
        clientY = (e as MouseEvent).clientY;
      }
      const screenWidth =
        document.documentElement.clientWidth ||
        document.body.clientWidth ||
        window.innerWidth;
      const screenHeight =
        document.documentElement.clientHeight ||
        document.body.clientHeight ||
        window.innerHeight;
      if (screenWidth < screenHeight) {
        // Portrait: horizontal splitter, compute by Y
        const rawY = clientY / screenHeight;
        const clampedY = Math.max(0.25, Math.min(0.75, rawY));
        setSplitRatio(clampedY);
        return;
      }
      // Landscape: vertical splitter, compute by X
      const rawX = clientX / screenWidth;
      const clampedX = Math.max(0.25, Math.min(0.75, rawX));
      setSplitRatio(clampedX);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove as any);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [setSplitRatio]);

  const onDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    // Disable while drawing (match HandSide behavior)
    if (!(timerDone && !isDrawingStarted)) return;
    dragging.current = true;
    document.body.style.cursor = isPortrait ? "row-resize" : "col-resize";
    document.body.style.userSelect = "none";
  };

  const isActive = !!(timerDone && !isDrawingStarted);
  const style: React.CSSProperties = isPortrait
    ? {
        position: "absolute",
        top: `${(splitRatio * 100).toFixed(3)}%`,
        left: "50%",
        transform: "translate(-50%, -50%) rotate(90deg)",
        width: "56px",
        height: "50px",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        zIndex: 1000,
        cursor: "row-resize",
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? "auto" : "none",
      }
    : {
        position: "absolute",
        left: `${(splitRatio * 100).toFixed(3)}%`,
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "56px",
        height: "50px",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        zIndex: 1000,
        cursor: "col-resize",
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? "auto" : "none",
      };

  return (
    <div
      className={isPortrait ? "horizontal-resizer" : "vertical-resizer"}
      style={style}
      onMouseDown={onDown as any}
      onTouchStart={onDown as any}
      title="Drag to resize"
      role="separator"
      aria-orientation={isPortrait ? "horizontal" : "vertical"}
      aria-label="Resize model and canvas"
    >
      <img
        src={splitIcon}
        alt="Resize"
        style={{
          width: "100%",
          height: "100%",
          userSelect: "none",
          pointerEvents: "none",
        }}
        draggable={false}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  splitRatio: state.splitRatio,
  isDrawingStarted: state.isDrawingStarted,
  timerDone: state.timerDone,
});
const mapDispatchToProps = (dispatch: any) => ({
  setSplitRatio: (ratio: number) => dispatch(setSplitRatio(ratio)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VerticalResizer);
